import { useEffect, useMemo, useRef, useState } from 'react';
import { geocode } from '../lib/api';
import { modeShorts } from '../lib/palette';
import { metresApart, normalize, searchStations } from '../lib/stationSearch';
import type { GeocodeResult, Stations } from '../lib/types';

interface Props {
  stations: Stations | null;
  onPick: (result: GeocodeResult) => void;
}

export function SearchBox({ stations, onPick }: Props) {
  const [query, setQuery] = useState('');
  const [geoResults, setGeoResults] = useState<GeocodeResult[]>([]);
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [highlight, setHighlight] = useState(0);
  const boxRef = useRef<HTMLDivElement | null>(null);
  const inputRef = useRef<HTMLInputElement | null>(null);

  // Debounced lookup; each keystroke aborts the previous request.
  useEffect(() => {
    const q = query.trim();
    if (q.length < 3) {
      setGeoResults([]);
      setLoading(false);
      setError(null);
      return;
    }
    const controller = new AbortController();
    const timer = setTimeout(async () => {
      setLoading(true);
      try {
        const found = await geocode(q, controller.signal);
        setGeoResults(found);
        setOpen(true);
        setError(found.length ? null : 'no matches in Berlin');
      } catch (err) {
        if ((err as Error).name === 'AbortError') return;
        setError(err instanceof Error ? err.message : 'lookup failed');
        setGeoResults([]);
      } finally {
        setLoading(false);
      }
    }, 250);
    return () => {
      clearTimeout(timer);
      controller.abort();
    };
  }, [query]);

  /*
   * Stations first, and without waiting for the network: they come from the
   * station list the app already has, so they land on the keystroke while the
   * geocoder is still debouncing. A geocoded row for the same place — Photon
   * knows the big stations too — is dropped as a duplicate.
   */
  const stationHits = useMemo(
    () => (stations ? searchStations(stations, query, 5) : []),
    [stations, query]
  );

  const results = useMemo(() => {
    const dupe = (r: GeocodeResult) =>
      stationHits.some((s) => {
        if (metresApart(r.lat, r.lon, s.lat, s.lon) > 250) return false;
        const a = normalize(r.label);
        const b = normalize(s.label);
        return a.includes(b) || b.includes(a);
      });
    return [...stationHits, ...geoResults.filter((r) => !dupe(r))].slice(0, 10);
  }, [stationHits, geoResults]);

  // A new query invalidates whatever row was highlighted.
  useEffect(() => {
    setHighlight(0);
  }, [query]);

  // Dismiss the dropdown on an outside click.
  useEffect(() => {
    const onDown = (ev: MouseEvent) => {
      if (boxRef.current && !boxRef.current.contains(ev.target as Node)) setOpen(false);
    };
    document.addEventListener('mousedown', onDown);
    return () => document.removeEventListener('mousedown', onDown);
  }, []);

  const choose = (r: GeocodeResult) => {
    onPick(r);
    setQuery('');
    setGeoResults([]);
    setOpen(false);
    inputRef.current?.blur();
  };

  const onKeyDown = (ev: React.KeyboardEvent) => {
    if (ev.key === 'Escape') {
      setOpen(false);
      return;
    }
    if (!open || !results.length) return;
    if (ev.key === 'ArrowDown') {
      ev.preventDefault();
      setHighlight((h) => (h + 1) % results.length);
    } else if (ev.key === 'ArrowUp') {
      ev.preventDefault();
      setHighlight((h) => (h - 1 + results.length) % results.length);
    } else if (ev.key === 'Enter') {
      ev.preventDefault();
      choose(results[highlight]);
    }
  };

  return (
    <div className="search" ref={boxRef}>
      <div className="search__field">
        <svg className="search__icon" viewBox="0 0 16 16" aria-hidden="true">
          <circle cx="6.8" cy="6.8" r="4.4" fill="none" stroke="currentColor" strokeWidth="1.6" />
          <path d="M10.2 10.2 14 14" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" />
        </svg>
        <input
          ref={inputRef}
          type="text"
          value={query}
          placeholder="Add a place by address or station…"
          aria-label="Search for an address or transit station in Berlin"
          autoComplete="off"
          spellCheck={false}
          onChange={(e) => {
            setQuery(e.target.value);
            setOpen(true);
          }}
          onFocus={() => results.length > 0 && setOpen(true)}
          onKeyDown={onKeyDown}
        />
        {loading && <span className="search__spinner" aria-label="searching" />}
      </div>
      {open && (results.length > 0 || error) && (
        <ul className="search__results" role="listbox">
          {error && !results.length && <li className="search__empty">{error}</li>}
          {results.map((r, i) => (
            <li key={`${r.lat},${r.lon},${i}`}>
              <button
                type="button"
                className={
                  `search__result${r.source === 'station' ? ' search__result--station' : ''}` +
                  `${i === highlight ? ' is-highlighted' : ''}`
                }
                role="option"
                aria-selected={i === highlight}
                onMouseEnter={() => setHighlight(i)}
                onClick={() => choose(r)}
              >
                <span className="search__result-label">
                  {r.source === 'station' && (
                    <span className="search__badges" aria-label="transit station">
                      {modeShorts(r.modes ?? 0).map((m) => (
                        <span key={m.short} className={`badge badge--${m.short}`} title={m.name}>
                          {m.short}
                        </span>
                      ))}
                    </span>
                  )}
                  {r.label}
                </span>
                {r.detail && <span className="search__result-detail">{r.detail}</span>}
              </button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
